import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";

import "./QuotationDetailDrawer.scss";
import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import Drawer from "components/v2/Drawer/Drawer";
import Divider from "components/v2/Divider/Divider";
import EmptyState from "components/v2/EmptyState/EmptyState";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import { ReactComponent as AlertIcon } from "assets/images/iconsV2/alert-circle-grey.svg";
import DrawerFooter from "components/v2/Drawer/components/DrawerFooter/DrawerFooter";
import QuotationDetailDrawerHeader from "./components/QuotationDetailDrawerHeader/QuotationDetailDrawerHeader";
import QuotationDetailQuoteInfo from "./components/QuotationDetailQuoteInfo";
import QuotationDetailRouteInfo from "./components/QuotationDetailRouteInfo/QuotationDetailRouteInfo";
import QuotationDetailServiceConfirmation from "./components/QuotationDetailServiceConfirmation/QuotationDetailServiceConfirmation";
import QuotationDetailCharges from "./components/QuotationDetailCharges/QuotationDetailCharges";
import QuotationFooter from "../QuotationCard/QuotationFooter/QuotationFooter";

function QuotationDetailDrawer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const quotationId = searchParams.get("detail");
  const { id: shipmentId } = useParams();
  const intl = useIntl();

  const { data: quotationData, isLoading } = useGetQuotations({
    shipmentId: shipmentId!,
    options: {
      enabled: !!shipmentId,
      refetchOnMount: false,
    },
  });
  const quotations = quotationData?.quotations || [];
  const currentQuotation = useMemo(
    () => quotations.find((quotation: any) => quotation.reference === quotationId),
    [quotations, quotationId],
  );

  const handleClose = () => {
    searchParams.delete("detail");
    setSearchParams(searchParams);
  };

  return (
    <div className="quotation-detail-drawer">
      <Drawer isOpen={!!quotationId} onClose={handleClose} size="lg" excludeSelectors={[".modal"]}>
        <QuotationDetailDrawerHeader onClose={handleClose} quotations={quotations} />
        {isLoading && <LoadingSkeleton rows={6} />}
        {!isLoading && currentQuotation && (
          <>
            <QuotationDetailQuoteInfo quotations={quotations} currentQuotation={currentQuotation} />
            <Divider />
            <QuotationDetailRouteInfo currentQuotation={currentQuotation} />
            <Divider className="my-xl" />
            <QuotationDetailServiceConfirmation currentQuotation={currentQuotation} />
            <QuotationDetailCharges currentQuotation={currentQuotation} />
            <DrawerFooter>
              <div className="d-flex justify-content-end w-100">
                <QuotationFooter quotation={currentQuotation} showViewDetailsButton={false} />
              </div>
            </DrawerFooter>
          </>
        )}
        {!isLoading && !currentQuotation && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <EmptyState
              noActions
              customIcon={
                <div className="">
                  <AlertIcon />
                </div>
              }
              title={intl.formatMessage({
                id: "general.couldNotLoadDetails",
                defaultMessage: "We couldn't load these details",
              })}
              description={intl.formatMessage({
                id: "general.tryAgainOrCheckBackLater",
                defaultMessage: "You can try again or check back later.",
              })}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default QuotationDetailDrawer;
